import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Package, Truck, Clock, ArrowRight, Home, FileText } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/services/api';

interface Order {
  orderId: string;
  totalAmount: number;
  status: string;
  createdDate: string;
  shippingAddress: string;
  phoneNumber: string;
  items: Array<{
    productName: string;
    quantity: number;
    price: number;
    primaryImageUrl?: string;
  }>;
}

const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const orderId = location.state?.orderId;
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId || !user.isAuthenticated) {
        setError('Order ID not found or user not authenticated');
        setLoading(false);
        return;
      }

      try {
        const response = await authFetch(`/api/orders/my-orders`);
        if (response.ok) {
          const orders = await response.json();
          const foundOrder = orders.find((o: Order) => o.orderId === orderId);
          
          if (foundOrder) {
            setOrder(foundOrder);
          } else {
            setError('Order not found');
          }
        } else {
          setError('Failed to fetch order details');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, user.isAuthenticated]);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              <div className="h-40 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-destructive mb-4">⚠️</div>
            <h1 className="text-2xl font-bold mb-2">Order Not Found</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          </div>
        </div>
      </Layout>
    );
  }

  const getDeliveryEstimate = () => {
    const orderDate = new Date(order.createdDate);
    const estimatedDelivery = new Date(orderDate);
    estimatedDelivery.setDate(orderDate.getDate() + 5); // 5 days estimate
    return estimatedDelivery.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-green-600 mb-2">
              Order Confirmed!
            </h1>
            <p className="text-lg text-muted-foreground mb-4">
              Thank you for your purchase. Your order has been successfully placed.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 inline-block">
              <p className="font-semibold text-green-800">
                Order ID: {order.orderId}
              </p>
              <p className="text-sm text-green-600">
                Amount Paid: ₹{order.totalAmount}
              </p>
            </div>
          </div>

          {/* Order Timeline */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Order Timeline
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Order Confirmed</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.createdDate).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <Package className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-muted-foreground">Processing</p>
                    <p className="text-sm text-muted-foreground">
                      We'll prepare your order for shipment
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <Truck className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-muted-foreground">Shipped</p>
                    <p className="text-sm text-muted-foreground">
                      Expected by {getDeliveryEstimate()}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">Order Items</h3>
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 pb-4 border-b last:border-b-0">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      {item.primaryImageUrl ? (
                        <img
                          src={item.primaryImageUrl}
                          alt={item.productName}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <Package className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">{item.productName}</h4>
                      <p className="text-sm text-muted-foreground">
                        Quantity: {item.quantity} • Price: ₹{item.price}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">₹{item.price * item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-3">Delivery Address</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="whitespace-pre-line">{order.shippingAddress}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Contact: {order.phoneNumber}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/orders')}
              className="flex items-center justify-center gap-2"
            >
              <FileText className="h-4 w-4" />
              View All Orders
            </Button>
            
            <Button 
              onClick={() => navigate('/')}
              className="flex items-center justify-center gap-2"
            >
              <Home className="h-4 w-4" />
              Continue Shopping
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Order Support */}
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="p-6 text-center">
              <h3 className="font-semibold mb-2 text-blue-800">Need Help?</h3>
              <p className="text-sm text-blue-700 mb-4">
                If you have any questions about your order, please contact our support team.
              </p>
              <Button variant="outline" size="sm" className="border-blue-300 text-blue-700">
                Contact Support
              </Button>
            </CardContent>
          </Card>

          {/* Order ID for reference */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Keep this order ID for your records: <strong>{order.orderId}</strong>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderConfirmationPage;